/**
 * CopyAnalyzer
 *
 * Premium AI tool to analyze and improve ad copy. Handles access gating,
 * local persistence, cancelable requests, and detailed result rendering.
 */
import React, { useState, useEffect, useRef } from 'react';
import { analyzeCopy } from '../../api/tools/toolsApi';
import { useToast } from '../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../components/shared/Footer';
import { HelpCircle, Crown, ChevronRight } from 'lucide-react';
import { ErrorMessage } from '../../components/ui/feedback/error-message.jsx';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { ButtonSpinner } from '../../components/ui/feedback/loading-spinner.jsx';
import { AILoadingModal } from '../../components/ui/modals/ai-loading-modal.jsx';
import SEOHead from '../../components/shared/SEOHead';

// Storage keys
const STORAGE_KEY_AD_COPY = 'refrral_ad_copy';
const STORAGE_KEY_ANALYSIS = 'refrral_ad_analysis';

// Tooltip component
function Tooltip({ text, children }) {
  const [isVisible, setIsVisible] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  
  // Show tooltip with delay to prevent flickering on accidental hover
  const showTooltip = () => {
    // Clear any existing timeout to prevent flickering
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set visibility first, then fade in
    setIsVisible(true);
    // Small delay before showing to prevent flash on quick mouse movements
    timeoutRef.current = setTimeout(() => {
      setOpacity(1);
    }, 50);
  };
  
  // Hide tooltip with transition
  const hideTooltip = () => {
    // Start fade out animation
    setOpacity(0);
    // Remove from DOM after transition completes
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 200); // Match transition duration
  };
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Calculate and set position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // On mobile, position tooltip centered and below
        setPosition({
          top: rect.bottom + 10,
          left: window.innerWidth / 2 - 140, // Approximate center
        });
      } else {
        // On desktop, position below the trigger
        setPosition({
          top: rect.bottom + 10,
          left: rect.left,
        });
      }
    }
  }, [isVisible]);
  
  // Portal element with transitions
  const tooltipElement = (
    <div 
      ref={tooltipRef}
      className="fixed z-[9999] bg-gray-800 text-white rounded-md shadow-lg transition-opacity duration-200"
      style={{ 
        top: `${position.top}px`, 
        left: `${position.left}px`,
        maxWidth: '280px',
        padding: '12px 16px',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        opacity: opacity,
        pointerEvents: 'none', // Prevents the tooltip from interfering with mouse events
      }}
    >
      <div 
        className="absolute bottom-full left-4" 
        style={{ 
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: '8px solid #1f2937',
        }}
      ></div>
      {text}
    </div>
  );

  return (
    <>
      <div 
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>
      {isVisible && createPortal(tooltipElement, document.body)}
    </>
  );
}

function CopyAnalyzer() {
  const [adCopy, setAdCopy] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [justCopied, setJustCopied] = useState(false);
  const [error, setError] = useState('');
  const abortControllerRef = useRef(null);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check user access to AI tools
  const isPremium = user?.planLevel === 'premium';
  const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;

  // All enterprise users get AI tools access (same as premium)
  const hasAIToolsAccess = isPremium || isEnterpriseUser;

  // Debug logging for enterprise access
  useEffect(() => {
    if (user && isEnterpriseUser) {
      console.log('CopyAnalyzer - Enterprise Access Debug:', {
        user: user,
        isEnterpriseUser,
        hasAIToolsAccess,
        enterpriseData: user?.enterpriseData
      });
    }
  }, [user, isEnterpriseUser, hasAIToolsAccess]);

  // Load saved state on component mount
  useEffect(() => {
    const savedAdCopy = localStorage.getItem(STORAGE_KEY_AD_COPY);
    const savedAnalysis = localStorage.getItem(STORAGE_KEY_ANALYSIS);
    
    if (savedAdCopy) {
      setAdCopy(savedAdCopy);
    }
    
    if (savedAnalysis) {
      try {
        setAnalysis(JSON.parse(savedAnalysis));
      } catch (e) {
        console.error('Failed to parse saved analysis:', e);
        localStorage.removeItem(STORAGE_KEY_ANALYSIS);
      }
    }
  }, []);

  // Save ad copy when it changes
  useEffect(() => {
    if (adCopy.trim() !== '') {
      localStorage.setItem(STORAGE_KEY_AD_COPY, adCopy);
    } else {
      localStorage.removeItem(STORAGE_KEY_AD_COPY);
    }
  }, [adCopy]);

  // Save analysis when it changes
  useEffect(() => {
    if (analysis) {
      localStorage.setItem(STORAGE_KEY_ANALYSIS, JSON.stringify(analysis));
    } else {
      localStorage.removeItem(STORAGE_KEY_ANALYSIS);
    }
  }, [analysis]);

  // Function to provide a more helpful error message
  const getHelpfulErrorMessage = (error) => {
    const errorMessage = error.message || String(error);
    
    // Check for common network/fetch errors
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network Error')) {
      return "Server connection failed. The server might be down or experiencing high traffic. Please try again later or try with shorter copy.";
    }
    
    // Check for specific HTTP status codes
    if (errorMessage.includes('401') || errorMessage.includes('403')) {
      return "Authentication error. Please sign in again and try once more.";
    }
    
    if (errorMessage.includes('500') || errorMessage.includes('502') || errorMessage.includes('503')) {
      return "Server error. Our system is currently having issues. Please try again later or try with different copy.";
    }
    
    // Default fallback with a suggestion
    return `${errorMessage} Please try again with different copy or at a later time.`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!adCopy.trim() || adCopy.trim().length < 10) {
      setError('Please enter at least 10 characters of ad copy to analyze.');
      return;
    }

    // Check if user has access to AI tools
    if (!hasAIToolsAccess) {
      if (isEnterpriseUser) {
        // Enterprise users without access go to app home, not pricing
        addToast('AI Tools are not enabled for your enterprise account', 'warning');
        navigate('/app/account', { replace: true });
      } else {
        // Non-enterprise users without access go to account page (not pricing)
        navigate('/app/account', { 
          state: { 
            fromFeature: true,
            returnPath: '/app/copyAnalyzer',
            featureName: 'Copy Analyzer'
          } 
        });
      }
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysis(null);
    setError('');
    
    try {
      const result = await analyzeCopy({ adCopy });
      
      // Normalize result to handle both old and new API response formats
      const normalizedResult = {
        // Handle scores with fallbacks, properly handling zero values
        attentionScore: result.attentionScore !== undefined ? result.attentionScore : 
                      (result.readabilityScore !== undefined ? result.readabilityScore : 70),
        clarityScore: result.clarityScore !== undefined ? result.clarityScore : 90,
        persuasivenessScore: result.persuasivenessScore !== undefined ? result.persuasivenessScore : 
                            (result.effectivenessScore !== undefined ? result.effectivenessScore : 65),
        
        // Handle evaluation text
        overallEvaluation: result.overallEvaluation || result.textAnalysis || '',
        
        // Handle suggestions and strengths
        suggestions: result.suggestions || [],
        strengths: result.strengths || [],
        
        // Old fields for backward compatibility
        textAnalysis: result.textAnalysis || result.overallEvaluation || '',
        readabilityScore: result.readabilityScore !== undefined ? result.readabilityScore : 
                        (result.attentionScore !== undefined ? result.attentionScore : 70),
        effectivenessScore: result.effectivenessScore !== undefined ? result.effectivenessScore : 
                          (result.persuasivenessScore !== undefined ? result.persuasivenessScore : 65),
        
        // Handle improved copy (new field) or fall back to suggestedAdCopy (old field)
        improvedCopy: result.improvedCopy || result.suggestedAdCopy || '',
        suggestedAdCopy: result.suggestedAdCopy || result.improvedCopy || ''
      };
      
      setAnalysis(normalizedResult);
    } catch (error) {
      console.error('Analysis error:', error);
      setError(getHelpfulErrorMessage(error));
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Function to clear all inputs and results
  const clearAll = () => {
    setAdCopy('');
    setAnalysis(null);
    localStorage.removeItem(STORAGE_KEY_AD_COPY);
    localStorage.removeItem(STORAGE_KEY_ANALYSIS);
  };

  // Handle copy to clipboard with visual feedback
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setJustCopied(true);
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setJustCopied(false);
    }, 2000);
  };

  // Function to render a score gauge
  const ScoreGauge = ({ score, label, color }) => {
    // Determine color class based on score
    const getColorClass = () => {
      if (!color) {
        if (score >= 80) return 'text-green-500 dark:text-green-400';
        if (score >= 60) return 'text-blue-500 dark:text-blue-400';
        if (score >= 40) return 'text-yellow-500 dark:text-yellow-400';
        return 'text-red-500 dark:text-red-400';
      }
      return color;
    };
    
    // Ensure score is treated as 0 when it's actually 0, not defaulting to something else
    const displayScore = score !== undefined && score !== null ? score : 0;
    
    return (
      <div className="flex flex-col items-center">
        <div className="relative w-52 h-52">
          {/* Background circle */}
          <svg className="w-full h-full" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="currentColor"
              strokeWidth="12"
              className="text-gray-200 dark:text-gray-700"
            />
            {/* Score circle */}
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="currentColor"
              strokeWidth="12"
              strokeDasharray={`${displayScore * 3.39} 339`}
              strokeDashoffset={-84.75}
              strokeLinecap="round"
              className={getColorClass()}
              transform="rotate(-90 60 60)"
            />
          </svg>
          {/* Score text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-5xl font-bold ${getColorClass()}`}>{displayScore}</span>
          </div>
        </div>
        <span className="mt-3 text-xl font-medium text-gray-700 dark:text-gray-300">{label}</span>
      </div>
    );
  };

  // Cancel the current AI request
  const handleCancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsAnalyzing(false);
    addToast('The copy analysis has been cancelled.', 'info');
  };

  return (
    <div className="p-4 sm:p-6 pb-20 w-full max-w-[1600px] mx-auto">
      <SEOHead pageType="app-copy-analyzer" />
      {/* Styles for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-in-out forwards;
          opacity: 0;
        }
      `}</style>
      
      {/* Premium feature notification - only show for users without access */}
      {!hasAIToolsAccess && (
        <div className="mb-6 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800/50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Crown className="h-5 w-5 text-purple-500 dark:text-purple-400 mr-3" />
              <p className="text-sm text-purple-800 dark:text-purple-300">
                Copy Analyzer is a premium feature. Upgrade to analyze your ad copy for better engagement.
              </p>
            </div>
            <button 
              onClick={() => navigate('/app/account', { state: { fromFeature: true, returnPath: '/app/copyAnalyzer', featureName: 'Copy Analyzer' }})}
              className="ml-4 flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-md text-xs font-medium"
            >
              Upgrade
              <ChevronRight className="ml-1 h-3 w-3" />
            </button>
          </div>
        </div>
      )}
      
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Copy Analyzer
          </h1>
        </div>
        <div className="text-gray-600 dark:text-gray-400 flex items-center">
          Analyze and optimize your ad copy for better engagement and conversion rates.
          <Tooltip 
            text={
              <div className="space-y-2">
                <p className="font-medium">What is Ad Copy?</p>
                <p>Ad copy refers to the text used in advertisements that aims to persuade viewers to take a specific action, such as making a purchase or signing up for a service. Effective ad copy is concise, compelling, and clearly communicates the value proposition of your product or service.</p>
                <p>Good ad copy typically includes strong headlines, clear benefits, and a compelling call-to-action (CTA).</p>
              </div>
            }
          >
            <HelpCircle className="ml-1.5 w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
          </Tooltip>
        </div>
      </div>
      
      <div className="bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-8">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="adCopy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enter your ad copy
            </label>
            <textarea
              id="adCopy"
              value={adCopy}
              onChange={(e) => setAdCopy(e.target.value)}
              className="w-full h-40 px-3 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900"
              placeholder="Enter your ad copy here (minimum 5 characters)..."
              disabled={isAnalyzing}
            ></textarea>
          </div>
          
          {/* Error message */}
          {error && (
            <ErrorMessage 
              message={error} 
              duration={3000} 
              onClose={() => setError('')}
            />
          )}
          
          {/* Information section */}
          <div className="mb-6 mt-2 rounded-lg p-4 bg-gray-50/50 dark:bg-gray-800/30 border border-gray-200/50 dark:border-gray-700/50">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">How the Copy Analyzer Works</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Our Copy Analyzer uses advanced AI to evaluate your political ad text and provide actionable insights. It scores your content on attention-grabbing power, clarity, and persuasiveness, highlights strengths, and offers specific suggestions for improvement. The system also generates an enhanced version of your copy to help you create more effective political messaging with minimal effort.
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            {adCopy.trim().length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                disabled={isAnalyzing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Clear
              </button>
            )}
            <button
              type="submit"
              disabled={isAnalyzing || adCopy.trim().length < 5}
              className={`px-4 py-2.5 rounded-lg text-white font-medium ${
                isAnalyzing || adCopy.trim().length < 5
                  ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' 
                  : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600'
              } transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
            >
              {isAnalyzing ? (
                <>
                  <ButtonSpinner />
                  Analyzing...
                </>
              ) : 'Analyze Copy'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Full-screen loading overlay */}
      <AILoadingModal 
        isOpen={isAnalyzing}
        message="Analyzing your copy..."
        subtitle="Our AI is evaluating your content for impact and effectiveness"
        onCancel={handleCancelRequest}
      />
      
      {/* Analysis results */}
      {analysis && !isAnalyzing && (
        <div className="bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6" 
             style={{ 
               animation: 'fadeIn 0.5s ease-in-out forwards',
               opacity: 0
             }}>
          
          {/* Score gauges */}
          <div className="flex flex-col md:flex-row justify-center gap-8 mb-12 py-6">
            <ScoreGauge 
              score={analysis.attentionScore !== undefined ? analysis.attentionScore : analysis.readabilityScore} 
              label="Attention" 
              color="text-blue-500 dark:text-blue-400" 
            />
            <ScoreGauge 
              score={analysis.clarityScore !== undefined ? analysis.clarityScore : 90} 
              label="Clarity" 
              color="text-purple-500 dark:text-purple-400" 
            />
            <ScoreGauge 
              score={analysis.persuasivenessScore !== undefined ? analysis.persuasivenessScore : analysis.effectivenessScore} 
              label="Persuasiveness" 
              color="text-green-500 dark:text-green-400" 
            />
          </div>
          
          {/* Text Analysis - Show if available */}
          {(analysis.textAnalysis || analysis.overallEvaluation) && (
            <div className="mb-8 bg-white dark:bg-transparent p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Analysis Summary
              </h3>
              
              <div className="text-gray-800 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                {analysis.textAnalysis || analysis.overallEvaluation}
              </div>
            </div>
          )}
          
          {/* Suggestions and strengths */}
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {/* Strengths */}
            <div className="bg-white dark:bg-transparent p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Strengths
              </h3>
              
              {analysis.strengths && analysis.strengths.length > 0 ? (
                <ul className="space-y-2">
                  {analysis.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-primary-600 dark:text-primary-400 mr-2">•</span>
                      <span className="text-gray-800 dark:text-gray-300">{strength}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 italic">
                  No specific strengths were identified.
                </p>
              )}
            </div>
            
            {/* Suggestions */}
            <div className="bg-white dark:bg-transparent p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Suggestions
              </h3>
              
              {analysis.suggestions && analysis.suggestions.length > 0 ? (
                <ul className="space-y-2">
                  {analysis.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-primary-600 dark:text-primary-400 mr-2">•</span>
                      <span className="text-gray-800 dark:text-gray-300">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 italic">
                  No specific suggestions were provided.
                </p>
              )}
            </div>
          </div>
          
          {/* Improved Copy - Only show if not empty */}
          {analysis.improvedCopy && analysis.improvedCopy.trim() !== "" && (
            <div className="mt-8 bg-white dark:bg-transparent p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
                <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Improved Copy
                </h3>
                <button
                  onClick={() => handleCopy(analysis.improvedCopy)}
                  className={`inline-flex self-start items-center px-3 py-1.5 border ${justCopied ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'} rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                >
                  {justCopied ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md text-gray-800 dark:text-gray-200 whitespace-pre-wrap max-h-60 overflow-y-auto">
                {analysis.improvedCopy}
              </div>
            </div>
          )}
          
          {/* For backward compatibility - Suggested Ad Copy - Only show if improvedCopy is not available */}
          {!analysis.improvedCopy && analysis.suggestedAdCopy && analysis.suggestedAdCopy.trim() !== "" && (
            <div className="mt-8 bg-white dark:bg-transparent p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
                <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Recommended Ad Copy
                </h3>
                <button
                  onClick={() => handleCopy(analysis.suggestedAdCopy)}
                  className={`inline-flex self-start items-center px-3 py-1.5 border ${justCopied ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'} rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                >
                  {justCopied ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-md text-gray-800 dark:text-gray-200 whitespace-pre-wrap max-h-60 overflow-y-auto">
                {analysis.suggestedAdCopy}
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={() => {
                setAnalysis(null);
                localStorage.removeItem(STORAGE_KEY_ANALYSIS);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Clear Results
            </button>
          </div>
        </div>
      )}

      {/* Add Footer component */}
      <Footer />
    </div>
  );
}

export default CopyAnalyzer; 